import {TYPE_CONVERT_UNIT, TYPE_GRAPH} from './type-graph.enum';
import {NoResults} from './no-results';


export type customLabelFunctionType = (value: string | number, formattedValue: any, valueAxis: any) => string | number;

export interface IbulletConfig {
  bullet?: string;
  bulletBorderAlpha?: number;
  bulletSize?: number;
  bulletAlpha?: number;
}

export interface IlineConfig {
  lineThickness?: number;
  fillAlphas?: number;
  lineAlpha?: number;
  lineColor?: string;
  useLineColorForBulletBorder?: boolean;
}

export interface IcolorGraph {
  [key: string]: string;
}

// export type IcustomUnit = { [K in TYPE_UNIT]?: string };

export interface IcategoryAxis {
  /**
   * Alpha value for categoryAxis/X --> 0, invisible; 1, visible
   */
  axisAlpha?: number;
  /**
   * Alpha value for categoryGrid --> 0, invisible; 1, visible
   */
  gridAlpha?: number;
  /**
   * It decides if the labels in categoryAxis/X exist --> true: exists; false: don't exist
   */
  labelsEnabled?: boolean;
  /**
   * It decides if the labels in categoryAxis/X are transparent - certain values for chartCursor need labels to exists.
   * So labes can exist, but they can aslo be transparent --> true: visible; false: transparent
   */
  labelsColor?: boolean;
  /**
   * Gives rotation to the labels in ategoryAxis/X
   */
  labelRotation?: number;
}

export interface IvalueAxis {
  /**
   * Alpha value for valueAxis/Y --> 0, invisible; 1, visible
   */
  axisAlpha?: number;
  /**
   * Alpha value for valueGrid --> 0, invisible; 1, visible
   */
  gridAlpha?: number;
  /**
   * It decides if the labels in valueAxis/Y exist --> true: exists; false: don't exist
   */
  labelsEnabled?: boolean;
  /**
   * Position of units in valueAxis/Y: default is right; options: left, right
   */
  unitPosition?: 'left' | 'right';
  /**
   * Units in valueAxis/Y
   */
  unit?: string;
  /**
   * It decides if the values are shown in logaritmic. If there is any value that is 0, it won't work; so the charts in the application
   * have a built in function 'isLogaritmic', in GenericChartModel', that transforms the values outside the amcharts library
   */
  logarithmic?: boolean;
  /**
   * min value in valueAxis/Y to display
   */
  minimum?: number;
  /**
   * max value in valueAxis/Y to display
   */
  maximum?: number;
  /**
   * precision in values - number of decimals -
   */
  precision?: number;
}

export interface IConfigGraphArg {
  /**
   * custom function for label
   * not implemented
   */
  customLabelFunction?: customLabelFunctionType;
  /**
   * custom function for balloon
   * not implemented
   */
  balloonCustomFunction?: boolean;
  /**
   * The way the bullets are being displayed
   */
  bulletConfig?: IbulletConfig;
  /**
   * The way the lines are being displayed
   */
  lineConfig?: IlineConfig;
  /**
   * If true, shows a default scrollbar in valueAxis/Y
   */
  chartScrollbar?: boolean;
  /**
   * Object that configures categoryAxis
   */
  categoryAxis?: IcategoryAxis;
  /**
   * Name for the categoryAxis/X;
   */
  categoryField?: string;
  /**
   * If true, shows a default scrollbar in valueAxis/Y
   */
  valueScrollbar?: boolean;
  /**
   * Object that configures categoryAxis
   */
  valueAxis?: IvalueAxis;
  /**
   * It decides if the function 'isLogaritmic', in GenericChartModel', transforms the values outside the amcharts library
   */
  customLogarithmic?: boolean;
  /**
   * It decides if the graph has chartCursor. Of this depends that there is a balloon in the graph if there are no bullets in it.
   * It gives the default configuration of chartCursor
   */
  chartCursor?: boolean;
  /**
   * It gives a built in configuration of chartCursor
   */
  balloonPointerOrientation?: boolean;
  /**
   * it gives a built in configuration for the text shown in the balloon
   */
  balloonText?: boolean;
  /**
   * Top margin for the graphic
   */
  marginTop?: number;
  /**
   * Bottom margin for the graphic
   */
  marginBottom?: number;
  /**
   * Right margin for the graphic
   */
  marginRight?: number;
  /**
   * Left margin for the graphic
   */
  marginLeft?: number;
  /**
   * Width of the div created for the graph
   */
  width?: string;
  /**
   * height of the div created for the graph
   */
  height?: string;
  /**
   * If true, it gives a built in legend for the graph. It is built in each type of graph
   */
  isLegend?: boolean;
  /**
   * If true, it makes a conversion in the categoryField in order to parse the data format
   */
  isTimeStamp?: boolean;
  /**
   * If true, the categoryAxis values will be sorted; if the categoryField is 'days', it will sort by days
   */
  isSortable?: boolean;
  /**
   * id used to create the div for the graph with an unic id
   */
  id?: number | string;
  /**
   * index used to create the div for the graph with an unic id
   */
  index?: number;
  /**
   * graphAlias used to create the div for the graph with an unic id
   */
  graphAlias?: string;
  /**
   * id used in the div for the graph
   */
  divId?: string;
  /**
   * used to convert units in balloon text
   */
  unitConverterType?: TYPE_CONVERT_UNIT;
  /**
   * used to convert units in valueAxis Labels
   */
  valueAxisConverterType?: TYPE_CONVERT_UNIT;
  /**
   * used to convert units in valueAxis Labels
   */
  colorGraph?: IcolorGraph;
}

export class ValuesSerieDetailGraph {
  constructor(public value, public key) {
  }
}

export class SerieDetailGraph {
  values: ValuesSerieDetailGraph[] = [];

  constructor(values: ValuesSerieDetailGraph[], public name = '') {
    values.forEach(value => {
      this.values.push(new ValuesSerieDetailGraph(value.value, value.key));
    });
  }
}

export class DetailGraph {
  title = '';
  xlabel: string;
  ylabel: string;
  series: SerieDetailGraph[] = [];

  constructor({title, xlabel, ylabel, series}:
                { title: string, xlabel: string, ylabel: string, series: SerieDetailGraph[] } | any) {
    this.title = title != null ? title : this.title;
    this.xlabel = xlabel != null ? xlabel : this.xlabel;
    this.ylabel = ylabel != null ? ylabel : this.ylabel;
    if (series) {
      series.forEach(data => {
        this.series.push(new SerieDetailGraph(data.values, data.name));
      });
    }
  }
}

export class ConfigGraph {
  customLabelFunction: customLabelFunctionType;
  balloonCustomFunction: boolean;
  bulletConfig?: IbulletConfig;
  lineConfig?: IlineConfig;
  chartScrollbar = false;
  valueScrollbar = false;
  valueAxis: IvalueAxis = {} as IvalueAxis;
  customLogarithmic = false;
  chartCursor = false;
  balloonPointerOrientation = false;
  balloonText = false;
  categoryField = 'key';
  categoryAxis: IcategoryAxis;
  marginTop = 0;
  marginBottom = 0;
  marginRight = 0;
  marginLeft = 0;
  width = '100%';
  height = '160px';
  isLegend = false;
  isTimeStamp = false;
  isSortable = false;
  id: number | string = 0;
  index = 0;
  graphAlias = '';
  colorGraph: IcolorGraph = {};
  divId = this.id.toString();

  unitConverterType: TYPE_CONVERT_UNIT = TYPE_CONVERT_UNIT.DEFAULT;
  valueAxisConverterType: TYPE_CONVERT_UNIT = TYPE_CONVERT_UNIT.DEFAULT;

  constructor({graph, config}: {graph: Graph, config?: IConfigGraphArg, name?: string }) {

    if (config) {
      this.customLabelFunction = config.customLabelFunction;
      this.balloonCustomFunction = config.balloonCustomFunction;
      this.bulletConfig = config.bulletConfig;
      this.lineConfig = config.lineConfig;
      this.valueAxis = this.valueAxis != null ? {...this.valueAxis, ...config.valueAxis} : this.valueAxis;
      this.valueScrollbar = config.valueScrollbar != null ? config.valueScrollbar : this.valueScrollbar;
      this.customLogarithmic = config.customLogarithmic != null ? config.customLogarithmic : this.customLogarithmic;
      this.chartCursor = config.chartCursor != null ? config.chartCursor : this.chartCursor;
      this.balloonPointerOrientation = config.balloonPointerOrientation != null ?
        config.balloonPointerOrientation :
        this.balloonPointerOrientation;
      this.balloonText = config.balloonText != null ? config.balloonText : this.balloonText;
      this.chartScrollbar = config.chartScrollbar != null ? config.chartScrollbar : this.chartScrollbar;
      this.categoryField = config.categoryField != null ? config.categoryField : this.categoryField;
      this.categoryAxis = config.categoryAxis != null ? config.categoryAxis : this.categoryAxis;
      this.marginTop = config.marginTop != null ? config.marginTop : this.marginTop;
      this.marginBottom = config.marginBottom != null ? config.marginBottom : this.marginBottom;
      this.marginRight = config.marginRight != null ? config.marginRight : this.marginRight;
      this.marginLeft = config.marginLeft != null ? config.marginLeft : this.marginLeft;
      this.width = config.width != null ? config.width : this.width;
      this.height = config.height != null ? config.height : this.height;
      this.isLegend = config.isLegend != null ? config.isLegend : this.isLegend;
      this.isTimeStamp = config.isTimeStamp != null ? config.isTimeStamp : this.isTimeStamp;
      this.isSortable = config.isSortable != null ? config.isSortable : this.isSortable;
      this.id = config.id != null ? config.id : this.id;
      this.index = config.index != null ? config.index : this.index;
      this.graphAlias = config.graphAlias != null ? config.graphAlias : this.graphAlias;
      this.divId = config.divId != null ? config.divId : this.id.toString() + this.graphAlias + this.index;
      this.unitConverterType = config.unitConverterType != null ? config.unitConverterType : this.unitConverterType;
      this.valueAxisConverterType = config.valueAxisConverterType != null ? config.valueAxisConverterType : this.valueAxisConverterType;
      this.colorGraph = config.colorGraph != null ? config.colorGraph : this.colorGraph;
    }
  }
}

export class Converter {
  protected static timeConverterFromSecondsToUnit(value, unit) {
    if (unit === 'days') {
      return Math.floor(value / (360 * 24));
    }
    return value;
  }

  protected static quantityConvert(quantity, decimals = 2, thousands = 3) {
    const regex = new RegExp('/B(?=(d{' + thousands + '})+(?!d))/g');
    if (Math.abs(quantity) < 1000) {
      return parseFloat(Math.abs(quantity).toFixed(decimals))
        .toString()
        .replace(regex, ',');
    }
    if (Math.abs(quantity) < 1000000 && Math.abs(quantity) >= 1000) {
      return (
        parseFloat((Math.abs(quantity) / 1000).toFixed(decimals))
          .toString()
          .replace(regex, ',') + ' k'
      );
    } else if (
      Math.abs(quantity) < 1000000000000 &&
      Math.abs(quantity) >= 1000000
    ) {
      return (
        parseFloat((Math.abs(quantity) / 1000000).toFixed(decimals))
          .toString()
          .replace(regex, ',') + ' Million'
      );
    } else {
      return (
        parseFloat((Math.abs(quantity) / 1000000000000).toFixed(decimals))
          .toString()
          .replace(regex, ',') + ' Billion'
      );
    }
  }
}

export class AxisConverter extends Converter {
  static secondsToDays(value, formattedValue, valueAxis): string {
    return this.timeConverterFromSecondsToUnit(value, valueAxis.unit) + ' ' + (valueAxis.unit || '');
  }

  static customFormat(value, formattedValue, valueAxis): string {
    return this.quantityConvert(value) + ' ' + (valueAxis.unit || '');
  }

  static formatValue(value, formattedValue, valueAxis): string {
    return value + ' ' + (valueAxis.unit || '');
  }

  static default(value, formattedValue, valueAxis): string {
    return value;
  }
}

export class UnitConverter extends Converter {
  static secondsToDays(value: string, unit: string): string {
    return this.timeConverterFromSecondsToUnit(value, unit) + ' ' + unit;
  }

  static customFormat(value: string, unit: string = ''): string {
    return this.quantityConvert(value) + ' ' + unit;
  }

  static formatValue(value: string, unit: string = ''): string {
    return value + ' ' + unit;
  }

  static default(value: string, unit: string): string {
    return value;
  }
}

export class Graph {
  id: number | string = 0;
  graph: DetailGraph;
  config: ConfigGraph;
  type: TYPE_GRAPH;
  noResults: NoResults;

  PRECISION_REQUIREMENTS = {};
  CSS_FOUR_COLORS_NAMES = ['#327ABD', '#DC665A', '#EBCB00', '#00B18F', '#1B2734'];
  CSS_COLOR_CORPORATIVE = ['#2e9eb8', '#5cb4c8', '#8ac9d7'];
  CORPORATIVE_COLORS = {
    GOOD: '#00B18F',
    MEDIUM: '#00B18F',
    WARN: '#EBCB00',
    HIGH: '#EBCB00',
    BAD: '#DC665A',
    CRITICAL: '#DC665A',
    LOW: '#327ABD'
  };

  constructor() {
  }
}

export class GraphFactory {
  static graphicLoad({graphData = null, name = '', id = 0, type = TYPE_GRAPH.LINE, config = null, noResults = null}:
        { graphData?: DetailGraph,
          name?: string,
          id?: number | string,
          type?: TYPE_GRAPH | string,
          config?: IConfigGraphArg,
          noResults?: NoResults }): Graph {
    const graph = new Graph();
    if (noResults && noResults.isError) {
      graph.noResults = noResults;
    } else {
      graph.noResults = noResults;
      graph.type = type as TYPE_GRAPH;
      graph.id = id;
      graph.graph = graphData;
      graph.config = new ConfigGraph({graph, config, name});
    }
    return graph;
  }
  static graphicCopy({id, graph, config, type, noResults}) {
    const graphTemp = new Graph();
    graphTemp.id = id;
    graphTemp.graph = graph;
    graphTemp.config = config;
    graphTemp.type = type;
    graphTemp.noResults = noResults;
    return graphTemp;
  }
}

